import { useState, useEffect, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Loader2, BarChart3, Download, Trash2, ShieldCheck } from "lucide-react";

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} style={{ padding: "12px 14px", borderRadius: 9, border: "1px solid var(--cardborder)", background: "var(--surface)" }}>
      <div className="font-display" style={{ fontSize: 22, fontWeight: 700, color: "var(--tprimary)", lineHeight: 1 }}>{value}</div>
      <div className="font-mono" style={{ fontSize: 10.5, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 6 }}>{label}</div>
    </div>
  );
}

export function AccountTools({ activeWsId, onDataDeleted }) {
  const [digest, setDigest] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(() => {
    api.get("/account/digest").then(({ data }) => setDigest(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load, activeWsId]);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hdg-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported", { description: "Your workspace data downloaded as JSON." });
    } catch (e) {
      toast.error(errMsg(e, "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  const deleteData = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete("/account/data");
      toast.success("Workspace content deleted", { description: `${data.brandsRemoved} brand(s) and all related content removed.` });
      setConfirm(false);
      onDataDeleted?.();
      load();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  const a = digest?.activity || {};
  const s = digest?.scheduled || {};

  return (
    <>
      <div className="panel-card" style={{ padding: 22 }} data-testid="settings-digest">
        <div className="flex items-center gap-2" style={{ marginBottom: 14 }}>
          <BarChart3 size={15} style={{ color: "var(--tsecondary)" }} />
          <div className="eyebrow" style={{ margin: 0 }}>Performance digest</div>
        </div>
        {!digest ? (
          <div className="flex items-center gap-2 font-body" style={{ fontSize: 13, color: "var(--tmuted)" }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat testId="digest-approved" label="approved" value={a.approved ?? 0} />
            <Stat testId="digest-approval-rate" label="approval rate" value={`${a.approvalRate ?? 0}%`} />
            <Stat testId="digest-scheduled" label="scheduled" value={s.total ?? 0} />
            <Stat testId="digest-brands" label="brands" value={digest.brands ?? 0} />
          </div>
        )}
      </div>

      <div className="panel-card" style={{ padding: 22 }} data-testid="settings-privacy">
        <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
          <ShieldCheck size={15} style={{ color: "var(--tsecondary)" }} />
          <div className="eyebrow" style={{ margin: 0 }}>Privacy &amp; data</div>
        </div>
        <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "0 0 16px" }}>
          Export everything this workspace stores, or permanently delete all its content.
        </p>
        <div className="flex flex-wrap gap-2.5">
          <button onClick={exportData} disabled={exporting} className="btn-secondary font-mono" data-testid="export-data-button" style={{ fontSize: 12, padding: "9px 14px" }}>
            {exporting ? <Loader2 size={14} className="spin" /> : <Download size={14} />} Export my data
          </button>
          {!confirm ? (
            <button onClick={() => setConfirm(true)} className="font-mono" data-testid="delete-data-button" style={{ fontSize: 12, padding: "9px 14px", borderRadius: 9, border: "1px solid var(--err)", background: "transparent", color: "var(--err)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>
              <Trash2 size={14} /> Delete all content
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-body" style={{ fontSize: 12.5, color: "var(--err)" }}>Delete everything permanently?</span>
              <button onClick={deleteData} disabled={deleting} className="font-mono" data-testid="delete-data-confirm" style={{ fontSize: 12, padding: "9px 14px", borderRadius: 9, border: "none", background: "var(--err)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>
                {deleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Yes, delete
              </button>
              <button onClick={() => setConfirm(false)} className="btn-secondary font-mono" data-testid="delete-data-cancel" style={{ fontSize: 12, padding: "9px 14px" }}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
